/* ── C6: External resource links dropdown ────────────────────── */

import { useEffect, useRef, useState } from 'react';

interface ResourceLinksProps {
  /** When true, programmatically opens the dropdown (e.g. from CommandPalette) */
  forceOpen?: boolean;
  /** Called after forceOpen is consumed so parent can reset the flag */
  onForceOpenConsumed?: () => void;
  /** Controlled mode: when provided, overrides internal open state */
  isOpen?: boolean;
  /** Controlled mode: called when the user closes the dropdown */
  onClose?: () => void;
  /** When true, hide the trigger button and use a fixed-position overlay for the panel */
  hideButton?: boolean;
}

const RESOURCES = [
  {
    label: 'EESS Registration Database',
    url: 'https://equipment.eess.gov.au/',
    desc: 'Electrical equipment safety registration',
  },
  {
    label: 'National Register of Certifiers',
    url: 'https://certifiers.eess.gov.au/',
    desc: 'Accredited certifiers & approvals',
  },
  {
    label: 'NCC Digital',
    url: 'https://ncc.abcb.gov.au/',
    desc: 'National Construction Code online',
  },
  {
    label: 'Standards Australia',
    url: 'https://www.standards.org.au/',
    desc: 'Official standards catalogue',
  },
  {
    label: 'ACCC Product Safety',
    url: 'https://www.productsafety.gov.au/',
    desc: 'Product recalls & safety standards',
  },
];

export default function ResourceLinks({ forceOpen, onForceOpenConsumed, isOpen: controlledOpen, onClose: controlledOnClose, hideButton = false }: ResourceLinksProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (v: boolean | ((prev: boolean) => boolean)) => {
    if (isControlled) {
      const next = typeof v === 'function' ? v(controlledOpen!) : v;
      if (!next) controlledOnClose?.();
    } else {
      setInternalOpen(v);
    }
  };
  const wrapRef = useRef<HTMLDivElement>(null);

  /* Open when parent requests it (e.g. from CommandPalette) */
  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      onForceOpenConsumed?.();
    }
  }, [forceOpen, onForceOpenConsumed]);

  /* Close dropdown on outside click */
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  /* Close on Escape */
  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open]);

  // When hideButton=true, render a fixed overlay panel instead of a dropdown
  if (hideButton) {
    if (!open) return null;
    return (
      <div
        ref={wrapRef}
        style={{
          position: 'fixed',
          bottom: '72px',
          right: '16px',
          zIndex: 300,
          width: '300px',
          background: 'var(--bg-card)',
          border: '1px solid var(--border-color)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
        role="dialog"
        aria-label="External resources"
        data-tour="external-resources"
      >
        <div className="resource-links-header">
          <span>External Resources</span>
          <button className="resource-links-close" onClick={() => setOpen(false)} aria-label="Close resources">✕</button>
        </div>
        <ul className="resource-links-list" role="list">
          {RESOURCES.map((r) => (
            <li key={r.url} role="listitem">
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="resource-link-item" onClick={() => setOpen(false)}>
                <span className="resource-link-label">{r.label}</span>
                <span className="resource-link-desc">{r.desc}</span>
                <svg className="resource-link-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                  <polyline points="15 3 21 3 21 9" />
                  <line x1="10" y1="14" x2="21" y2="3" />
                </svg>
              </a>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="resource-links-wrap" data-tour="external-resources">
      <button
        className={`toolbar-btn toolbar-btn--labeled${open ? ' toolbar-btn--active' : ''}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="External resources"
        aria-expanded={open}
        aria-haspopup="true"
        title="External resources"
      >
        {/* Globe icon */}
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="2" y1="12" x2="22" y2="12" />
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        <span className="toolbar-btn-label">Resources</span>
      </button>

      {open && (
        <div
          className="resource-links-dropdown"
          role="dialog"
          aria-label="External resources"
        >
          <div className="resource-links-header">
            <span>External Resources</span>
            <button
              className="resource-links-close"
              onClick={() => setOpen(false)}
              aria-label="Close resources"
            >
              ✕
            </button>
          </div>
          <ul className="resource-links-list" role="list">
            {RESOURCES.map((r) => (
              <li key={r.url} role="listitem">
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="resource-link-item"
                  onClick={() => setOpen(false)}
                >
                  <span className="resource-link-label">{r.label}</span>
                  <span className="resource-link-desc">{r.desc}</span>
                  {/* External link icon */}
                  <svg
                    className="resource-link-icon"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
